import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    CircularProgress,
    Alert,
    Paper,
    Card,
    CardContent,
    Grid,
    Avatar,
    Chip,
    Divider,
    Stack,
    Badge,
    IconButton,
    Tooltip,
    Link,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    MenuItem
} from '@mui/material';
import CustomDatePicker from '../Calendar/CustomDatePicker';
import api from '../../services/api';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PaymentIcon from '@mui/icons-material/Payment';
import PoolIcon from '@mui/icons-material/Pool';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventBusyIcon from '@mui/icons-material/EventBusy';

// Définir l'interface mise à jour pour une réservation
interface ProductDetails {
    duration: string;
    minAge: number;
    maxAge: number;
    maxDepth: number;
    included: string[];
    prerequisites: string[];
}

interface ProductVariant {
    id: string;
    name: string;
    price: number;
    duration: string;
    time: string;
    _id: string;
}

interface Category {
    _id: string;
    name: string;
    description: string;
    imageUrl: string;
    slug: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

interface Product {
    details: ProductDetails;
    availability: {
        daysOfWeek: string[];
        endDate: string;
        startDate: string;
    };
    _id: string;
    name: string;
    description: string;
    price: number;
    imageUrl: string;
    category: Category;
    variants: ProductVariant[];
    createdAt: string;
    updatedAt: string;
    __v: number;
    slug: string;
}

interface Reservation {
    _id: string;
    customer: {
        name: string;
        email: string;
        phone: string;
    };
    product: Product;
    date: string;
    time?: string;
    sessionTime?: string;
    participants: number;
    status: string;
    paymentStatus?: string;
    payment?: {
        status: string;
        amount: number;
        method: string;
        paymentIntentId?: string;
    };
    activity?: string;
    level?: string;
    createdAt: string;
    updatedAt: string;
    __v?: number;
    timeSlot?: {
        startTime: string;
        endTime: string;
    };
}

// Fonction pour formater la date
const formatDate = (dateStr: string) => {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
    };
    return new Date(dateStr).toLocaleDateString('fr-FR', options);
};

// Fonction pour obtenir la couleur du statut
const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'confirmed':
        case 'validé':
            return 'success';
        case 'pending':
        case 'en attente':
            return 'warning';
        case 'cancelled':
        case 'annulé':
            return 'error';
        default:
            return 'default';
    }
};

// Fonction pour traduire le statut en français
const translateStatus = (status: string) => {
    switch (status.toLowerCase()) {
        case 'confirmed':
            return 'Confirmée';
        case 'pending':
            return 'En attente';
        case 'cancelled':
            return 'Annulée';
        default:
            return status;
    }
};

const getPaymentStatusColor = (status?: string) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
        case 'paid':
        case 'payé':
            return 'success';
        case 'pending':
        case 'en attente':
            return 'warning';
        case 'cancelled':
        case 'refunded':
        case 'remboursé':
            return 'error';
        default:
            return 'default';
    }
};

// Fonction pour obtenir la couleur du niveau
const getLevelColor = (level?: string) => {
    if (!level) return 'default';

    switch (level.toLowerCase()) {
        case 'débutant':
            return 'info';
        case 'intermédiaire':
            return 'warning';
        case 'avancé':
            return 'error';
        default:
            return 'default';
    }
};


interface Slot {
    _id: string;
    date: string;
    productId: string;
    time: string;
    capacity: number;
    isAvailable: boolean;
}

// Fonction pour calculer les places restantes dans le créneau
const calculateSlotRemainingPlaces = (
    reservations: Reservation[], 
    currentReservation: Reservation,
    slots: Slot[]
) => {
    // Trouver le créneau correspondant
    const slotTime = currentReservation.timeSlot?.startTime || currentReservation.time;
    
    // Normaliser les dates pour la comparaison (format YYYY-MM-DD)
    const reservationDate = new Date(currentReservation.date).toISOString().split('T')[0];
    
    const slot = slots.find(s => {
        const slotDate = new Date(s.date).toISOString().split('T')[0];
        return slotDate === reservationDate && s.time === slotTime;
    });
    
    const slotCapacity = slot?.capacity || 6; // Utiliser la capacité du créneau ou 6 par défaut
    
    // Trouver toutes les réservations confirmées pour le même créneau
    const sameSlotReservations = reservations.filter(res => {
        const resDate = new Date(res.date).toISOString().split('T')[0];
        const resTime = res.timeSlot?.startTime || res.time;
        return resDate === reservationDate &&
               resTime === slotTime &&
               res.status === 'confirmed';
    });
    
    // Calculer le total de participants pour ce créneau
    const totalParticipants = sameSlotReservations.reduce((sum, res) => sum + res.participants, 0);
    
    return Math.max(0, slotCapacity - totalParticipants);
};

const AdminReservations: React.FC = () => {
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [allReservations, setAllReservations] = useState<Reservation[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string>('');
    const [products, setProducts] = useState<any[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [slots, setSlots] = useState<Slot[]>([]);

    useEffect(() => {
        fetchReservations();
        fetchProducts();
        fetchSlots();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await api.get('/products');
            const data = response.data;
            setProducts(data);
            if (data.length > 0) {
                setSelectedProductId(data[0]._id);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
        }
    };

    const fetchSlots = async () => {
        try {
            const response = await api.get('/planning/slots/all');
            setSlots(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des créneaux:', error);
        }
    };

    // Filtrer les réservations lorsque la date sélectionnée change
    useEffect(() => {
        if (selectedDate) {
            const filteredReservations = allReservations.filter(reservation => {
                const reservationDate = new Date(reservation.date);
                return (
                    reservationDate.getDate() === selectedDate.getDate() &&
                    reservationDate.getMonth() === selectedDate.getMonth() &&
                    reservationDate.getFullYear() === selectedDate.getFullYear()
                );
            });
            setReservations(filteredReservations);
            setPage(0); // Revenir à la première page après filtrage
        } else {
            setReservations(allReservations);
        }
    }, [selectedDate, allReservations]);

    const fetchReservations = async () => {
        try {
            setLoading(true);
            const response = await api.get('/reservations');
            const data: Reservation[] = response.data;
            // Trier les réservations par date (les plus récentes d'abord)
            const sortedData = data.sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            setAllReservations(sortedData);
            setReservations(sortedData);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Une erreur est survenue lors de la récupération des données.');
            console.error("Erreur de fetch:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleClearDate = () => {
        setSelectedDate(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewDetails = (reservation: Reservation) => {
        setSelectedReservation(reservation);
    };

    const handleConfirmReservation = async () => {
        if (!selectedReservation) return;

        setActionLoading(true);
        try {
            await api.put(`/reservations/${selectedReservation._id}/confirm`);

            // Mettre à jour la liste des réservations
            fetchReservations();
            setConfirmDialogOpen(false);
        } catch (err: any) {
            setError(err.message || 'Une erreur est survenue lors de la confirmation de la réservation.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelReservation = async () => {
        if (!selectedReservation) return;

        setActionLoading(true);
        try {
            await api.put(`/reservations/${selectedReservation._id}/cancel`);

            // Mettre à jour la liste des réservations
            fetchReservations();
            setCancelDialogOpen(false);
        } catch (err: any) {
            setError(err.message || 'Une erreur est survenue lors de l\'annulation de la réservation.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteReservation = async () => {
        if (!selectedReservation) return;

        setActionLoading(true);
        try {
            await api.delete(`/reservations/${selectedReservation._id}`);

            // Mettre à jour la liste des réservations
            fetchReservations();
            setDeleteDialogOpen(false);
        } catch (err: any) {
            setError(err.message || 'Une erreur est survenue lors de la suppression de la réservation.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{
                    m: 3,
                    p: 2,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <strong>Erreur:</strong> {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{
                mb: 4,
                fontWeight: 'bold',
                color: 'primary.main',
                textAlign: 'center',
                fontSize: { xs: '1.8rem', sm: '2.2rem' }
            }}>
                Gestion des Réservations
            </Typography>

            {/* Filtres */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    label="Produit"
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    size="small"
                    sx={{ width: 200 }}
                >
                    <MenuItem value="">
                        <em>Tous les produits</em>
                    </MenuItem>
                    {products.map((product) => (
                        <MenuItem key={product._id} value={product._id}>
                            {product.name}
                        </MenuItem>
                    ))}
                </TextField>

                <CustomDatePicker
                    label="Filtrer par date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    productId={selectedProductId}
                    mode="reservations"
                    fullWidth={false}
                    required={false}
                />

                {selectedDate && (
                    <Tooltip title="Effacer le filtre">
                        <IconButton onClick={handleClearDate} size="small">
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {selectedDate ? `Filtré: ${formatDate(selectedDate.toISOString())}` : 'Toutes les dates'}
                </Typography>
            </Box>

            {reservations.length === 0 ? (
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#f9f9f9',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="h6">
                        {selectedDate ? 'Aucune réservation pour cette date.' : 'Aucune réservation trouvée.'}
                    </Typography>
                    {selectedDate && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleClearDate}
                            sx={{ mt: 2 }}
                        >
                            Voir toutes les réservations
                        </Button>
                    )}
                </Paper>
            ) : (
                <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <Table stickyHeader aria-label="table des réservations">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Téléphone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Activité</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Horaire</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Participants</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Paiement</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reservations
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((reservation) => (
                                        <TableRow key={reservation._id} hover>
                                            <TableCell>{reservation._id.slice(-6)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {reservation.customer.name}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {reservation.customer.email}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {reservation.customer.phone}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{reservation.product.name}</TableCell>
                                            <TableCell>{formatDate(reservation.date)}</TableCell>
                                            <TableCell>
                                                {reservation.timeSlot
                                                    ? reservation.timeSlot.startTime
                                                    : (reservation.time || "N/A")}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<GroupIcon />}
                                                    label={`${reservation.participants}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {calculateSlotRemainingPlaces(allReservations, reservation, slots)} places restantes
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={translateStatus(reservation.status)}
                                                    color={getStatusColor(reservation.status) as any}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {reservation.payment ? (
                                                    <Chip
                                                        label={
                                                            reservation.payment.status === 'paid'
                                                                ? 'Payé'
                                                                : (reservation.payment.status === 'pending'
                                                                    ? 'En attente'
                                                                    : reservation.payment.status)
                                                        }
                                                        color={getPaymentStatusColor(reservation.payment.status) as any}
                                                        size="small"
                                                    />
                                                ) : (
                                                    "N/A"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title="Voir détails">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewDetails(reservation)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {reservation.status === 'pending' && (
                                                        <Tooltip title="Confirmer">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => {
                                                                    setSelectedReservation(reservation);
                                                                    setConfirmDialogOpen(true);
                                                                }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {reservation.status !== 'cancelled' && (
                                                        <Tooltip title="Annuler">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                    setSelectedReservation(reservation);
                                                                    setCancelDialogOpen(true);
                                                                }}
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Tooltip title="Supprimer">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setSelectedReservation(reservation);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={reservations.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Lignes par page:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
                    />
                </Paper>
            )}

            {/* Dialogs de confirmation */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                aria-labelledby="confirm-dialog-title"
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirmer la réservation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir confirmer cette réservation ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} disabled={actionLoading}>
                        Annuler
                    </Button>
                    <Button onClick={handleConfirmReservation} color="primary" disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={24} /> : 'Confirmer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                aria-labelledby="cancel-dialog-title"
            >
                <DialogTitle id="cancel-dialog-title">
                    Annuler la réservation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir annuler cette réservation ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}>
                        Non
                    </Button>
                    <Button onClick={handleCancelReservation} color="error" disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={24} /> : 'Oui, annuler'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Supprimer la réservation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer définitivement cette réservation ? Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
                        Annuler
                    </Button>
                    <Button onClick={handleDeleteReservation} color="error" disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={24} /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog détails de la réservation */}
            <Dialog
                open={!!selectedReservation}
                onClose={() => setSelectedReservation(null)}
                maxWidth="md"
                fullWidth
                aria-labelledby="reservation-details-title"
            >
                {selectedReservation && (
                    <>
                        <DialogTitle id="reservation-details-title" sx={{ bgcolor: 'primary.main', color: 'white' }}>
                            Détails de la réservation #{selectedReservation._id.slice(-6)}
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon /> Client
                                    </Typography>
                                    <Box sx={{ ml: 3, mb: 3 }}>
                                        <Typography variant="body1">{selectedReservation.customer.name}</Typography>
                                        <Typography variant="body2">Email: {selectedReservation.customer.email}</Typography>
                                        <Typography variant="body2">Téléphone: {selectedReservation.customer.phone}</Typography>
                                    </Box>

                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CalendarTodayIcon /> Date et Heure
                                    </Typography>
                                    <Box sx={{ ml: 3, mb: 3 }}>
                                        <Typography variant="body1">{formatDate(selectedReservation.date)}</Typography>
                                        <Typography variant="body2">
                                            Horaire: {selectedReservation.timeSlot
                                                ? selectedReservation.timeSlot.startTime
                                                : (selectedReservation.time || "Non spécifié")}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Activité
                                    </Typography>
                                    <Box sx={{ ml: 3, mb: 3 }}>
                                        <Typography variant="body1">{selectedReservation.product.name}</Typography>
                                        <Typography variant="body2">
                                            Nombre de participants: {selectedReservation.participants}
                                        </Typography>
                                        {selectedReservation.level && (
                                            <Typography variant="body2">Niveau: {selectedReservation.level}</Typography>
                                        )}
                                    </Box>

                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Statut et Paiement
                                    </Typography>
                                    <Box sx={{ ml: 3 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2">Statut:</Typography>
                                            <Typography variant="body2">Statut:</Typography>
                                            <Chip
                                                label={translateStatus(selectedReservation.status)}
                                                color={getStatusColor(selectedReservation.status) as any}
                                                size="small"
                                            />
                                        </Box>

                                        {selectedReservation.payment && (
                                            <>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2">Paiement:</Typography>
                                                    <Chip
                                                        label={selectedReservation.payment.status === 'paid' ? 'Payé' : selectedReservation.payment.status}
                                                        color={getPaymentStatusColor(selectedReservation.payment.status) as any}
                                                        size="small"
                                                    />
                                                </Box>
                                                <Typography variant="body2">
                                                    Montant: {selectedReservation.payment.amount} €
                                                </Typography>
                                                <Typography variant="body2">
                                                    ID de transaction: {selectedReservation.payment.paymentIntentId}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Méthode: {selectedReservation.payment.method === 'card' ? 'Carte bancaire' : selectedReservation.payment.method}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSelectedReservation(null)}>
                                Fermer
                            </Button>
                            {selectedReservation.status === 'pending' && (
                                <Button
                                    onClick={() => {
                                        setConfirmDialogOpen(true);
                                    }}
                                    color="success"
                                    variant="contained"
                                >
                                    Confirmer
                                </Button>
                            )}
                            {selectedReservation.status !== 'cancelled' && (
                                <Button
                                    onClick={() => {
                                        setCancelDialogOpen(true);
                                    }}
                                    color="error"
                                    variant="contained"
                                >
                                    Annuler
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default AdminReservations;